// Configuração de condutor (NBR 5410, tabelas 36 a 39) e agrupamento (tabela 42) de circuitos e alimentadores.
// As opções válidas vêm de GET /electrical/circuits/data; aqui ficam só os padrões e as regras de consistência.
// A quantidade de condutores carregados é calculada pela API (tabela 46): aqui só se usa a coluna correspondente
// para filtrar métodos e disposições.

export const DEFAULT_CONDUCTOR = { material: 'copper', insulation: 'PVC', method: 'B1' }
export const DEFAULT_GROUPING = { reference: 1, circuitCount: 1 }

const FALLBACK_UNSUPPORTED_METHODS = ['D', 'G']
const FALLBACK_METHODS = ['A1', 'A2', 'B1', 'B2', 'C', 'D', 'E', 'F']

export const ARRANGEMENT_LABELS = {
  touching: 'Justapostos',
  trefoil: 'Trifólio',
  'flat-touching': 'No mesmo plano, justapostos',
  'horizontal-spaced': 'Espaçados na horizontal',
  'vertical-spaced': 'Espaçados na vertical',
}

// A API calcula a quantidade de condutores carregados; valores antigos são descartados.
function withoutLoadedConductors(conductor = {}) {
  const copy = { ...conductor }
  delete copy.loadedConductors

  return copy
}

export function createDefaultConfig() {
  return { conductor: { ...DEFAULT_CONDUCTOR }, grouping: { ...DEFAULT_GROUPING } }
}

export function getUnsupportedMethods(groupingOptions) {
  return groupingOptions?.unsupportedMethods ?? FALLBACK_UNSUPPORTED_METHODS
}

// Coluna de capacidade usada por um circuito terminal (sempre 2 condutores carregados).
export function getTerminalColumnCount(conductorOptions) {
  return conductorOptions?.terminalLoadedConductors ?? 2
}

// Coluna de capacidade usada pelo alimentador de um quadro. Com neutro carregado a API usa a coluna de 3.
export function getFeederColumnCount(conductorOptions, supplyType) {
  return conductorOptions?.feederLoadedConductors?.[supplyType] ?? (supplyType === 'single-phase' ? 2 : 3)
}

// Quantidade de condutores carregados considerada pela API no alimentador (2, 3 ou 4).
export function getFeederLoadedCount(conductorOptions, supplyType, neutralLoaded) {
  if (supplyType === 'three-phase' && neutralLoaded) return conductorOptions?.neutralLoadedConductors ?? 4

  return getFeederColumnCount(conductorOptions, supplyType)
}

export function getConfigurationsFor(conductorOptions, method, columnCount) {
  return (conductorOptions?.configurations || []).filter(
    (item) => item.method === method && item.loadedConductors === columnCount
  )
}

// Métodos que têm coluna de capacidade para a contagem automática (ex.: G não existe com 2 condutores).
export function getAvailableMethods(conductorOptions, columnCount) {
  if (!conductorOptions?.configurations) {
    return columnCount === 2 ? FALLBACK_METHODS : [...FALLBACK_METHODS, 'G']
  }

  return [
    ...new Set(
      conductorOptions.configurations.filter((item) => item.loadedConductors === columnCount).map((item) => item.method)
    ),
  ]
}

export function getArrangements(conductorOptions, method, columnCount) {
  return getConfigurationsFor(conductorOptions, method, columnCount)
    .map((item) => item.arrangement)
    .filter(Boolean)
}

export function getAllowedCases(groupingOptions, method) {
  return (groupingOptions?.cases || []).filter((item) => item.methods.includes(method))
}

// Torna a configuração coerente com a coluna automática: remove método, disposição e caso incompatíveis.
export function reconcileConfig(config, conductorOptions, groupingOptions, columnCount) {
  const conductor = withoutLoadedConductors(config.conductor)
  const methods = getAvailableMethods(conductorOptions, columnCount)

  if (!methods.includes(conductor.method)) conductor.method = DEFAULT_CONDUCTOR.method

  const arrangements = getArrangements(conductorOptions, conductor.method, columnCount)

  if (arrangements.length === 0) {
    // Sem opções carregadas, só F/G têm disposição; mantém o valor existente.
    if (conductorOptions?.configurations || !['F', 'G'].includes(conductor.method)) delete conductor.arrangement
  } else if (!arrangements.includes(conductor.arrangement)) {
    conductor.arrangement = arrangements[0]
  }

  const { groupFactor, ...rest } = config

  if (getUnsupportedMethods(groupingOptions).includes(conductor.method)) {
    return { ...rest, conductor, grouping: null, groupFactor: groupFactor ?? 1 }
  }

  const allowed = getAllowedCases(groupingOptions, conductor.method).map((item) => item.reference)
  let reference = config.grouping?.reference ?? DEFAULT_GROUPING.reference

  if (allowed.length > 0 && !allowed.includes(reference)) reference = allowed[0]

  return {
    ...rest,
    conductor,
    grouping: { reference, circuitCount: config.grouping?.circuitCount ?? DEFAULT_GROUPING.circuitCount },
  }
}

export function applyConductorChange(config, patch, conductorOptions, groupingOptions, columnCount) {
  return reconcileConfig(
    { ...config, conductor: { ...config.conductor, ...patch } },
    conductorOptions,
    groupingOptions,
    columnCount
  )
}

export function applyGroupingChange(config, patch) {
  return { ...config, grouping: { ...config.grouping, ...patch } }
}

// Ajusta o alimentador de um quadro à alimentação da instalação: coluna, disposição e neutro carregado.
export function reconcileBoard(board, supplyType, conductorOptions, groupingOptions) {
  const reconciled = reconcileConfig(
    board,
    conductorOptions,
    groupingOptions,
    getFeederColumnCount(conductorOptions, supplyType)
  )

  return { ...reconciled, neutralLoaded: supplyType === 'three-phase' && Boolean(board.neutralLoaded) }
}

export function isDefaultConfig(config) {
  const { conductor, grouping } = config

  return (
    conductor?.material === DEFAULT_CONDUCTOR.material &&
    conductor?.insulation === DEFAULT_CONDUCTOR.insulation &&
    conductor?.method === DEFAULT_CONDUCTOR.method &&
    !conductor?.arrangement &&
    grouping?.reference === DEFAULT_GROUPING.reference &&
    Number(grouping?.circuitCount) === DEFAULT_GROUPING.circuitCount
  )
}

export function summarizeConfig(config) {
  const { conductor, grouping } = config

  if (!conductor) return '-'

  const parts = [
    conductor.method,
    conductor.insulation,
    ...(conductor.arrangement ? [ARRANGEMENT_LABELS[conductor.arrangement] || conductor.arrangement] : []),
    grouping
      ? `caso ${grouping.reference}, ${grouping.circuitCount} circuito${Number(grouping.circuitCount) === 1 ? '' : 's'}`
      : `Fa manual ${config.groupFactor ?? 1}`,
    ...(config.neutralLoaded ? ['neutro carregado'] : []),
  ]

  return `${parts.join(' · ')}${isDefaultConfig(config) && !config.neutralLoaded ? ' (padrão)' : ''}`
}

// Devolve a mensagem do primeiro problema encontrado, ou null se a configuração puder ser enviada.
export function validateConfig(config) {
  if (config.grouping) {
    const count = Number(config.grouping.circuitCount)

    if (!Number.isInteger(count) || count < 1) return 'a quantidade de circuitos agrupados deve ser um inteiro positivo'
  } else if (!(Number(config.groupFactor ?? 1) > 0)) {
    return 'o fator de agrupamento manual deve ser maior que zero'
  }

  return null
}

// Campos de condutor/agrupamento no formato aceito pela API. Nunca envia groupFactor junto com grouping
// nem a quantidade de condutores carregados, que a API calcula.
export function buildConductorPayload(config) {
  const conductor = withoutLoadedConductors(config.conductor)

  return {
    conductor,
    ...(config.grouping
      ? {
          grouping: {
            reference: Number(config.grouping.reference),
            circuitCount: Number(config.grouping.circuitCount),
          },
        }
      : config.groupFactor !== undefined && { groupFactor: Number(config.groupFactor) }),
  }
}

// Completa circuitos/quadros de arquivos antigos. `legacyFactorDropped` indica que um fator manual diferente de 1
// foi substituído pelo agrupamento padrão (B1, caso 1, um circuito).
export function normalizeLoadedItem(item) {
  const { groupFactor, grouping, ...rest } = item
  const hasConductor = Boolean(item.conductor)
  const conductor = withoutLoadedConductors(hasConductor ? item.conductor : DEFAULT_CONDUCTOR)
  const manualOnly = FALLBACK_UNSUPPORTED_METHODS.includes(conductor.method)
  let legacyFactorDropped = false

  if (!hasConductor) {
    legacyFactorDropped = groupFactor !== undefined && Number(groupFactor) !== 1
  }

  if (manualOnly) {
    return { item: { ...rest, conductor, grouping: null, groupFactor: groupFactor ?? 1 }, legacyFactorDropped }
  }

  return {
    item: { ...rest, conductor, grouping: !hasConductor || !grouping ? { ...DEFAULT_GROUPING } : grouping },
    legacyFactorDropped,
  }
}
